import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { ThemeProvider } from './lib/theme';
import { ConfirmProvider } from './components/ui/ConfirmProvider';
import ModernLayout from './components/ModernLayout';
import Login from './pages/Login';
const Landing = lazy(() => import('./pages/Landing'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

// Lazy-loaded page components
const Onboarding = lazy(() => import('./pages/Onboarding'));
const CommandCenter = lazy(() => import('./pages/CommandCenter'));
const Debts = lazy(() => import('./pages/Debts'));
const MoneyHub = lazy(() => import('./pages/MoneyHub'));
const DebtDetail = lazy(() => import('./pages/DebtDetail'));
const Settings = lazy(() => import('./pages/Settings'));
const Employees = lazy(() => import('./pages/payroll/Employees'));
const EmployeeDetail = lazy(() => import('./pages/payroll/EmployeeDetail'));
const Timesheet = lazy(() => import('./pages/payroll/Timesheet'));
const Paychecks = lazy(() => import('./pages/payroll/Paychecks'));
const PayrollTaxSummary = lazy(() => import('./pages/payroll/PayrollTaxSummary'));
const PaystubUpload = lazy(() => import('./pages/payroll/PaystubUpload'));
const BankAccounts = lazy(() => import('./pages/taxes/BankAccounts'));
const UploadCSV = lazy(() => import('./pages/taxes/UploadCSV'));
const CategoryRules = lazy(() => import('./pages/taxes/CategoryRules'));
const Transactions = lazy(() => import('./pages/taxes/Transactions'));
const TaxSummary = lazy(() => import('./pages/taxes/TaxSummary'));
const Form1099Received = lazy(() => import('./pages/taxes/Form1099Received'));
const NonTaxableIncome = lazy(() => import('./pages/taxes/NonTaxableIncome'));
const SmartCategorize = lazy(() => import('./pages/taxes/SmartCategorize'));
const Clients = lazy(() => import('./pages/invoicing/Clients'));
const ClientDetail = lazy(() => import('./pages/invoicing/ClientDetail'));
const InvoicesList = lazy(() => import('./pages/invoicing/Invoices'));
const InvoiceEditor = lazy(() => import('./pages/invoicing/InvoiceEditor'));
const InvoiceDetail = lazy(() => import('./pages/invoicing/InvoiceDetail'));
const RecurringInvoices = lazy(() => import('./pages/invoicing/RecurringInvoices'));
const Capture = lazy(() => import('./pages/documents/Capture'));
const DocumentHistory = lazy(() => import('./pages/documents/History'));
const DocumentReview = lazy(() => import('./pages/documents/Review'));
const ChartOfAccounts = lazy(() => import('./pages/accounting/ChartOfAccounts'));
const JournalEntries = lazy(() => import('./pages/accounting/JournalEntries'));
const AccountLedger = lazy(() => import('./pages/accounting/AccountLedger'));
const ReportsHub = lazy(() => import('./pages/reports/Reports'));
const ProfitLoss = lazy(() => import('./pages/reports/ProfitLoss'));
const BalanceSheet = lazy(() => import('./pages/reports/BalanceSheet'));
const CashFlow = lazy(() => import('./pages/reports/CashFlow'));
const TrialBalance = lazy(() => import('./pages/reports/TrialBalance'));
const Products = lazy(() => import('./pages/inventory/Products'));
const ProductDetail = lazy(() => import('./pages/inventory/ProductDetail'));
const Vendors = lazy(() => import('./pages/inventory/Vendors'));
const PurchaseOrders = lazy(() => import('./pages/inventory/PurchaseOrders'));
const POEditor = lazy(() => import('./pages/inventory/POEditor'));
const PODetail = lazy(() => import('./pages/inventory/PODetail'));
const ContractorsList = lazy(() => import('./pages/contractors/Contractors'));
const ContractorDetail = lazy(() => import('./pages/contractors/ContractorDetail'));
const Report1099 = lazy(() => import('./pages/contractors/Report1099'));
const TaxRates = lazy(() => import('./pages/sales-tax/TaxRates'));
const SalesTaxSummary = lazy(() => import('./pages/sales-tax/SalesTaxSummary'));
const Bills = lazy(() => import('./pages/Bills'));
const Accounts = lazy(() => import('./pages/Accounts'));
const MileageLog = lazy(() => import('./pages/mileage/MileageLog'));
const MileageSummary = lazy(() => import('./pages/mileage/MileageSummary'));
const Team = lazy(() => import('./pages/settings/Team'));
const AuditLog = lazy(() => import('./pages/settings/AuditLog'));

function PublicLanding() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-slate-400">Loading...</div>;
  if (user && user.businessId) return <Navigate to="/" replace />;
  if (user && !user.businessId) return <Navigate to="/onboarding" replace />;
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-screen text-slate-400">Loading...</div>}>
      <Landing />
    </Suspense>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/welcome" />;
  if (!user.businessId) return <Navigate to="/onboarding" />;
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-64 text-slate-400">Loading...</div>}>
      {children}
    </Suspense>
  );
}

function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (user.businessId) return <Navigate to="/" />;
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-64 text-slate-400">Loading...</div>}>
      {children}
    </Suspense>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <ConfirmProvider>
        <AuthProvider>
          <Routes>
            <Route path="/welcome" element={<PublicLanding />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/forgot-password"
              element={
                <Suspense fallback={null}>
                  <ForgotPassword />
                </Suspense>
              }
            />
            <Route
              path="/reset-password"
              element={
                <Suspense fallback={null}>
                  <ResetPassword />
                </Suspense>
              }
            />
            <Route
              path="/onboarding"
              element={
                <OnboardingRoute>
                  <Onboarding />
                </OnboardingRoute>
              }
            />
            <Route
              element={
                <ProtectedRoute>
                  <ModernLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<CommandCenter />} />
              <Route path="/money" element={<MoneyHub />} />
              <Route path="/debts" element={<Debts />} />
              <Route path="/debts/:id" element={<DebtDetail />} />
              <Route path="/payroll/employees" element={<Employees />} />
              <Route path="/payroll/employees/:id" element={<EmployeeDetail />} />
              <Route path="/payroll/timesheet" element={<Timesheet />} />
              <Route path="/payroll/paychecks" element={<Paychecks />} />
              <Route path="/payroll/tax-summary" element={<PayrollTaxSummary />} />
              <Route path="/payroll/upload-paystub" element={<PaystubUpload />} />
              <Route path="/taxes/bank-accounts" element={<BankAccounts />} />
              <Route path="/taxes/upload" element={<UploadCSV />} />
              <Route path="/taxes/category-rules" element={<CategoryRules />} />
              <Route path="/taxes/transactions" element={<Transactions />} />
              <Route path="/taxes/summary" element={<TaxSummary />} />
              <Route path="/taxes/1099" element={<Form1099Received />} />
              <Route path="/taxes/non-taxable" element={<NonTaxableIncome />} />
              <Route path="/taxes/smart-categorize" element={<SmartCategorize />} />
              <Route path="/invoicing/clients" element={<Clients />} />
              <Route path="/invoicing/clients/:id" element={<ClientDetail />} />
              <Route path="/invoicing/invoices" element={<InvoicesList />} />
              <Route path="/invoicing/recurring" element={<RecurringInvoices />} />
              <Route path="/invoicing/invoices/new" element={<InvoiceEditor />} />
              <Route path="/invoicing/invoices/:id" element={<InvoiceDetail />} />
              <Route path="/invoicing/invoices/:id/edit" element={<InvoiceEditor />} />
              <Route path="/accounting/chart-of-accounts" element={<ChartOfAccounts />} />
              <Route path="/accounting/journal" element={<JournalEntries />} />
              <Route path="/accounting/accounts/:id" element={<AccountLedger />} />
              <Route path="/reports" element={<ReportsHub />} />
              <Route path="/reports/profit-loss" element={<ProfitLoss />} />
              <Route path="/reports/balance-sheet" element={<BalanceSheet />} />
              <Route path="/reports/cash-flow" element={<CashFlow />} />
              <Route path="/reports/trial-balance" element={<TrialBalance />} />
              <Route path="/inventory/products" element={<Products />} />
              <Route path="/inventory/products/:id" element={<ProductDetail />} />
              <Route path="/inventory/vendors" element={<Vendors />} />
              <Route path="/inventory/vendors/:id" element={<Vendors />} />
              <Route path="/inventory/purchase-orders" element={<PurchaseOrders />} />
              <Route path="/inventory/purchase-orders/new" element={<POEditor />} />
              <Route path="/inventory/purchase-orders/:id" element={<PODetail />} />
              <Route path="/inventory/purchase-orders/:id/edit" element={<POEditor />} />
              <Route path="/taxes/sales-tax-rates" element={<TaxRates />} />
              <Route path="/taxes/sales-tax-summary" element={<SalesTaxSummary />} />
              <Route path="/accounts" element={<Accounts />} />
              <Route path="/bills" element={<Bills />} />
              <Route path="/mileage/log" element={<MileageLog />} />
              <Route path="/mileage/summary" element={<MileageSummary />} />
              <Route path="/contractors" element={<ContractorsList />} />
              <Route path="/contractors/1099-report" element={<Report1099 />} />
              <Route path="/contractors/:id" element={<ContractorDetail />} />
              <Route path="/documents/capture" element={<Capture />} />
              <Route path="/documents/history" element={<DocumentHistory />} />
              <Route path="/documents/:id/review" element={<DocumentReview />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/team" element={<Team />} />
              <Route path="/settings/audit-log" element={<AuditLog />} />
            </Route>
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </AuthProvider>
      </ConfirmProvider>
    </ThemeProvider>
  );
}
